import { Controller } from "@cedarjs/forms";
import { faWarning } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { BaseSelectProps, Checkbox, FormControl, FormControlLabel, FormHelperText, InputAdornment, InputLabel, MenuItem, Rating, Select, Slider, TextField, Typography } from "@mui/material";
import { DateTimePicker, renderTimeViewClock } from "@mui/x-date-pickers-pro";
import dayjs from "dayjs";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { getWarningFromTresholds } from "src/const/Tresholds";
import { convertToFloatWithDefaultPrecision } from "src/const/Units";
import { useConfigurationStore, useDateStore } from "src/lib/store";
import InputDecimal from "../InputDecimal/InputDecimal";
import InputNumber from "../InputNumber/InputNumber";
import InputPercentage from "../InputPercentage/InputPercentage";

export const ControllerDecimal = ({ name, error, setValue, control, required = false, readonly = false, label = null, className = "", decimalScale = 3, rules = {}, ...props }) => {
  const { t } = useTranslation();
  const tresholds = useConfigurationStore(state => state.tresholds?.[name]) 
  return (
    <Controller name={name} render={({ field }) => {
      const warning = getWarningFromTresholds(tresholds, field.value)
      return (
      <FormControl error={!!error} className={className}>
        <TextField
          {...field}
          {...props}
          required={required}
          error={typeof error?.isError == "boolean" ? error.isError : !!error}
          value={typeof field.value !== "undefined" ? field.value : ""}
          // onReset={(event) => {debugger}}
          label={t(`labels.${label || name}`, {defaultValue: (label || name)})}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setValue(name, Number.isNaN(parseFloat(event.target.value)) ? null : event.target.value)
          }}
          slotProps={
            {
              input: {
                inputComponent: InputDecimal as any,
                endAdornment: <InputAdornment position="end">{t(`units.${label || name}`, {defaultValue: ''})}</InputAdornment>,
              },
              htmlInput: {
                'min': 0,
                ...(readonly ? { readOnly: readonly, style: { cursor: 'not-allowed' } } : {}),
                decimalScale,
                name
              }
            }
          }
        />
        {!!error && <FormHelperText error={typeof error?.isError == "boolean" ? error.isError : true}>{error?.type == "required" ? t('status.mandatory') : error.message}</FormHelperText>}
        {!!warning && <FormHelperText style={{color: warning.color}}><FontAwesomeIcon icon={faWarning} className="mr-1"/>{warning.message}</FormHelperText>}
      </FormControl>
    )}}
      control={control}
      rules={{
        ...(required ? { required: t('instructions.doSomething', { action: t('actions.give') }) + t(`labels.${name}`) } : {}),
        ...(tresholds?.minimum ? { min: {value: tresholds.minimum, message : t("status.belowMinimum", {name:t(`labels.${name}`), value:tresholds.minimum})}} : {}),
        ...(tresholds?.maximum ? { max: {value: tresholds.maximum, message : t("status.aboveMaximum", {name:t(`labels.${name}`), value:tresholds.maximum})}} : {}),
        ...rules
      }}
    />
  )
}


export const ControllerInt = ({ name, error, setValue, control, required = false, readonly = false, label = null, className = "", rules = {}, allowNegative = false, ...props }) => {
  const { t } = useTranslation();
  const tresholds = useConfigurationStore(state => state.tresholds?.[name]) 
  return (
    <Controller name={name} render={({ field }) => {
      const warning = getWarningFromTresholds(tresholds, field.value)
      return (
      <FormControl error={!!error} className={className}>
        <TextField
          {...field}
          {...props}
          required={required}
          label={t(`labels.${label || name}`, {defaultValue: (label || name)})}
          value={typeof field.value !== "undefined" ? field.value : ""}
          {...(!allowNegative ? {'min': 0} : {})}
          slotProps={
            {
              input: {
                inputComponent: InputNumber as any,
                endAdornment: <InputAdornment position="end">{t(`units.${label || name}`, {defaultValue:""})}</InputAdornment>,
              },
              htmlInput: {
                ...(!allowNegative ? {'min': 0} : {}),
                ...(readonly ? { readOnly: readonly, style: { cursor: 'not-allowed' } } : {})
              }
            }
          }
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setValue(name, Number.isNaN(parseInt(event.target.value)) ? null : +event.target.value)
          }}
        />
        {!!error && <FormHelperText error>{error?.type == "required" ? t('status.mandatory') : error.message}</FormHelperText>}
        {!!warning && <FormHelperText style={{color: warning.color}}><FontAwesomeIcon icon={faWarning} className="mr-1"/>{warning.message}</FormHelperText>}
      </FormControl>
    )}}
      control={control}
      rules={{
        ...(required ? { required: t('instructions.doSomething', { action: t('actions.give') }) + t(`labels.${name}`) } : {}),
        ...(tresholds?.minimum ? { min: {value: tresholds.minimum, message : t("status.belowMinimum", {name:t(`labels.${name}`), value:tresholds.minimum})}} : {}),
        ...(tresholds?.maximum ? { max: {value: tresholds.maximum, message : t("status.aboveMaximum", {name:t(`labels.${name}`), value:tresholds.maximum})}} : {}),
        ...rules,
      }}
    />
  )
}


export const ControllerPercentage = ({ name, error, label = null, setValue, defaultValue, control, disabled = false, isControlled = false, required, readonly = false, className = "",...props}) => {
  const { t } = useTranslation();
  const tresholds = useConfigurationStore(state => state.tresholds?.[name]) 
  return (
    <Controller name={name} render={({ field }) => (
      <FormControl error={!!error} className={className}>
        <TextField
          {...(isControlled ? field : {})}
          {...props}
          required={required}
          label={t(`labels.${label || name}`, {defaultValue: (label || name)})}
          disabled={disabled}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setValue(name, event.target.value)
          }}
          defaultValue={convertToFloatWithDefaultPrecision(defaultValue * 100)}

          
          slotProps={
            {
              input: {
                inputComponent: InputPercentage as any,
              },
              htmlInput: {
                ...(readonly ? { readOnly: readonly, style: { cursor: 'not-allowed' } } : {}),
                name: name
              }
            }
          }

        />
        {!!error && <FormHelperText error>{error?.type == "required" ? t('status.mandatory') : error.message}</FormHelperText>}
      </FormControl>
    )}
      control={control}
      rules={{
        ...(required ? { required: t('instructions.doSomething', { action: t('actions.give') }) + t(`labels.${name}`) } : {}),
        ...(tresholds?.minimum ? { min: {value: tresholds.minimum, message : t("status.belowMinimum", {name:t(`labels.${name}`), value:tresholds.minimum})}} : {}),
        ...(tresholds?.maximum ? { max: {value: tresholds.maximum, message : t("status.aboveMaximum", {name:t(`labels.${name}`), value:tresholds.maximum})}} : {}),
      }}
    />
  )
}



export const ControllerTextArea = ({ name, label = null, error, control, required = false,  className = "", ...props }) => {
  const { t } = useTranslation();
  return (
    <Controller name={name} render={({ field }) => (
      <FormControl error={!!error} className={className}>
        <TextField
          {...field}
          required={required}
          label={t(`labels.${label || name}`, {defaultValue: label})}
          value={field.value || ""}
          placeholder={t('placeholders.description')}
          multiline
          minRows={4}
          maxRows={4}
          inputProps={{ style: { resize: 'both' } }}
          {...props}
        />
        {!!error && <FormHelperText error>{error?.type == "required" ? t('status.mandatory') : error.message}</FormHelperText>}
      </FormControl>
    )}
      control={control}
      rules={required ? { required: t('instructions.doSomething', { action: t('actions.give') }) + t(`labels.${name}`) } : {}}
    />
  )
}



export const ControllerDate = ({ name, label = null, error, control, required, readOnly = false, className = "", disableFuture = true, shouldChangeGlobalDate = true, rules = {}, onChange = null, onOpen = null, minDateTime = null, maxDateTime = null}) => {
  const { t } = useTranslation();
  const setDate = useDateStore(state => state.setDate);
  return (
    <Controller name={name} render={({ field }) => (
      <FormControl error={!!error} className={className}>
        <DateTimePicker
          {...field}
          ampm={false}
          format="DD/MM/YYYY HH:mm"
          viewRenderers={{
            hours: renderTimeViewClock,
            minutes: renderTimeViewClock,
            seconds: renderTimeViewClock,
          }}
          value={dayjs(field.value)}
          onAccept={(value) => value.isValid() && shouldChangeGlobalDate && setDate(value)}
          onOpen={onOpen}
          //https://github.com/mui/mui-x/issues/8375#issuecomment-1532038082
          slotProps={{
            textField: {
              onBlur: (event) => {
                const date = dayjs(field.value)
                //https://github.com/mui/mui-x/issues/5391#issuecomment-1174971724
                //Inspiration : https://github.com/mui/mui-x/issues/8375#issuecomment-1532038082
                if(date.isValid()) {
                    field.onChange(date.toISOString())
                    shouldChangeGlobalDate && setDate(date)
                }
              }
            }
            }
          }
          onChange={(value, _context) => {
            field.onChange(value,_context);
            typeof onChange == "function" && onChange(value, _context)
          }}
          minDateTime={minDateTime ? dayjs(minDateTime) : null}
          maxDateTime={maxDateTime ? dayjs(maxDateTime) : null}
          disableFuture={disableFuture}
          label={label || t(`labels.${name}`)}
          {...(readOnly ? { readOnly: readOnly, sx: { cursor: 'not-allowed' } } : {})}
        />
        {!!error && <FormHelperText error>{error?.type == "required" ? t('status.mandatory') : error.message}</FormHelperText>}
      </FormControl>
    )}
      control={control}
      rules={{
        ...(required ? { required: t('instructions.doSomething', { action: t('actions.give') }) + t(`labels.${name}`) } : {}),
        ...rules,
      }}
    />
  )
}

const DEFAULT_MARKS = 5



export const ControllerSlider = ({ name, error, control, required = false }) => {
  const { t } = useTranslation();

  let marks = [],
    labels = t(`marks.${name}`, { returnObjects: true });

  for (let index = 0; index < DEFAULT_MARKS; index++) {
    marks.push({ value: index, label: labels[index] })
  }
  return (
    <Controller name={name} render={({ field }) => (
      <FormControl error={!!error}>
        <Typography>{t(`labels.${name}`)}</Typography>
        <Slider
          {...field}
          valueLabelDisplay="auto"
          valueLabelFormat={(value: number) => labels[value]}
          marks
          step={1}
          min={0}
          max={DEFAULT_MARKS - 1}
        />
        {!!error && <FormHelperText error>{error?.type == "required" ? t('status.mandatory') : error.message}</FormHelperText>}
      </FormControl>
    )}
      control={control}
      rules={required ? { required: t('instructions.doSomething', { action: t('actions.give') }) + t(`labels.${name}`) } : {}}
    />
  )
}

const MARKS = [{ value: 0, label: "0%" }, { value: 0.25, label: "25%" }, { value: 0.50, label: "50%" }, { value: 0.75, label: "75%" }, { value: 1, label: "100%" }]

export const ControllerSliderPercentage = ({ name, error, control, required = false }) => {
  const { t } = useTranslation();

  return (
    <Controller name={name} render={({ field }) => (
      <FormControl error={!!error}>
        <Typography>{t(`labels.${name}`)}</Typography>
        <Slider
          {...field}
          valueLabelDisplay="auto"
          valueLabelFormat={(value: number) => Math.round(value * 100) + "%"}
          marks={MARKS}
          step={0.01}
          min={0}
          max={1}
        />
        {!!error && <FormHelperText error>{error?.type == "required" ? t('status.mandatory') : error.message}</FormHelperText>}
      </FormControl>
    )}
      control={control}
      rules={required ? { required: t('instructions.doSomething', { action: t('actions.give') }) + t(`labels.${name}`) } : {}}
    />
  )
}
/**
 * 
 * @param name - string, 
 * @param error - string, 
 * @param control - string, 
 * @param required - string = false, 
 * @param icon - string = null, 
 * @param emptyIcon - string = null, 
 * @param colors - {filled: string, hover: string}
 * @param label - string - override name label
 * @returns 
 */
export const ControllerRating = ({ name, error, control, setValue, required = false, icon = null, emptyIcon = null, colors = null, label = null }) => {
  const { t } = useTranslation();
  const getLabel = (value:number) => typeof value == "number" ? t(`marks.${name}.${Math.floor(4.5- value)}`): ""

  return (
    <Controller name={name} render={({ field }) => (
      <FormControl error={!!error}>
        <Typography>{t(`labels.${label || name}`)}</Typography>
        <div className="flex items-center gap-2">
        <Rating
          {...field}
          onChange={(event, newValue) => {
            setValue(name, newValue);
          }}
          precision={0.5}
          size="large"
          {...(icon ? { icon } : {})}
          {...(emptyIcon ? { emptyIcon } : {})}
          {...(colors ? {
            sx: {
              '& .MuiRating-iconFilled': {
                color: colors.filled,
              },
              '& .MuiRating-iconHover': {
                color: colors.hover,
              },
            }
          } : {})}
          max={DEFAULT_MARKS - 1}
        />
        {!!error && <FormHelperText error>{error?.type == "required" ? t('status.mandatory') : error.message}</FormHelperText>}
        <Typography variant="caption">{getLabel(field.value)}</Typography>
        </div>
      </FormControl>
    )}
      control={control}
      rules={required ? { required: t('instructions.doSomething', { action: t('actions.give') }) + t(`labels.${name}`) } : {}}
    />
  )
}


interface ControllerSelectProps extends BaseSelectProps { 
  name: string,
  data?: any[],
  menuItemRender?: (data:any) => any,
  error,
  control,
  alternateOption?: any,
  className?: string,
  id?: string,
  children?: ReactNode
}
export const ControllerSelect = ({ name, data = [], menuItemRender = (data) => data.id, error, control, required = false, label = null, alternateOption = null, className = "", readOnly = false, multiple = false, size = "medium" as const, id = null, ...props}:ControllerSelectProps) => {
  const { t } = useTranslation();

  return (
    <Controller name={name} render={({ field }) => (
      //@ts-ignore
      <FormControl error={!!error} className={className} id={id} size={size} {...props}>
        <InputLabel id={name}>{t(`labels.${label || name}`)}</InputLabel>
        <Select
          {...field}
          //Setting Select as controlled directly, and setting MUI's default value (empty string) here so top level form's defaultValues are not wrongly typed
          value={typeof field.value != "undefined"  ? field.value : (multiple ? [] : "")}
          labelId={name}
          label={t(`labels.${label || name}`, {defaultValue: (label || name)})}
          readOnly={readOnly}
          multiple={multiple}
          //To keep the menu from being viewport height
          MenuProps={{
            slotProps: {
              paper: {
                style: {
                  maxHeight: 200,
                }
              }
            }
          }}
        >
          {alternateOption}
          {props.children || data.map((item, index) => (
            <MenuItem value={item.id} key={item.id}>
              {menuItemRender(item)}
            </MenuItem>
          ))}
        </Select>
        {!!error && <FormHelperText error>{error?.type == "required" ? t('status.mandatory') : error.message}</FormHelperText>}
      </FormControl>
    )}
      control={control}
      rules={required ? { required: t('instructions.doSomething', { action: t('actions.give') }) + t(`labels.${name}`) } : {}}
    />
  )
}


export const ControllerCheckbox = ({ name, error, control, required = false, className = ""}) => {
  const { t } = useTranslation();

  return (
    <Controller name={name} render={({ field }) => (
      <FormControl error={!!error} className={className}>
        <FormControlLabel
          labelPlacement="start"
          control={
            <Checkbox
              {...field}
              checked={field.value} // Set the checked value based on the field value
            />
          }
          label={t(`labels.${name}`)}
        />
        {!!error && <FormHelperText error>{error?.type == "required" ? t('status.mandatory') : error.message}</FormHelperText>}
      </FormControl>
    )}
      control={control}
      rules={required ? { required: t('instructions.doSomething', { action: t('actions.give') }) + t(`labels.${name}`) } : {}}
    />
  )
}

export const ControllerText = ({ name, label = name, error, control, required = false, readonly = false, className = "", ...props }) => {
  const { t } = useTranslation();
  return (
    <Controller name={name} render={({ field }) => (
      <FormControl error={!!error} className={className}>
        <TextField
          {...field}
          {...props}
          required={required}
          error={typeof error?.isError == "boolean" ? error.isError : !!error}
          value={field.value || ""}
          label={t(`labels.${label}`)}
        />
        {!!error && <FormHelperText error>{error?.type == "required" ? t('status.mandatory') : error.message}</FormHelperText>}
      </FormControl>
    )}
      control={control}
      rules={required ? { required: t('instructions.doSomething', { action: t('actions.give') }) + t(`labels.${name}`) } : {}}
    />
  )
}